<template>
  <BaseSideModal :width="'960px'" :show-footer="true" @close="$emit('close')">
    <template #title>
      <span>{{ $t('carriers.import.preview.Title') }}</span>
    </template>

    <!-- In the body, not the title slot: BaseSideModal sizes its scroll area as
         calc(100% - 172px), a hard-coded one-line header. -->
    <p class="policy-preview__subtitle">
      {{ carrierName }} · {{ $t('carriers.import.preview.NotYetInYourBook') }}
      <template v-if="row.caratulaUrl">
        · <a class="policy-preview__doc" :href="row.caratulaUrl" target="_blank" rel="noopener">
          {{ $t('carriers.import.snapshot.Caratula') }}
        </a>
      </template>
    </p>

    <BasePreloader v-if="loading" color="purple" size="l"/>

    <template v-else-if="incoming">
      <section class="policy-preview__band">
        <p class="policy-preview__band-title">{{ $t('carriers.import.preview.Holder') }}</p>
        <div class="policy-preview__grid">
          <div v-for="fact in holderFacts" :key="fact.key" class="policy-preview__fact">
            <span class="caption color--dark-purple">{{ fact.label }}</span>
            <span class="body-text color--black-purple policy-preview__value">
              {{ fact.value || '—' }}
            </span>
          </div>
        </div>
      </section>

      <section class="policy-preview__band">
        <p class="policy-preview__band-title">{{ $t('carriers.import.preview.Policy') }}</p>
        <div class="policy-preview__grid">
          <div v-for="fact in policyFacts" :key="fact.key" class="policy-preview__fact">
            <span class="caption color--dark-purple">{{ fact.label }}</span>
            <span class="body-text color--black-purple policy-preview__value">
              {{ fact.value || '—' }}
            </span>
          </div>
        </div>
      </section>

      <!-- The insured vehicle as the carrier lists it; only the parts it gave. -->
      <section v-if="vehicleFacts.length" class="policy-preview__band">
        <p class="policy-preview__band-title">{{ $t('adminTitles.VehicleSection') }}</p>
        <div class="policy-preview__grid">
          <div v-for="fact in vehicleFacts" :key="fact.key" class="policy-preview__fact">
            <span class="caption color--dark-purple">{{ fact.label }}</span>
            <span class="body-text color--black-purple policy-preview__value">{{ fact.value }}</span>
          </div>
        </div>
      </section>

      <!-- The carrier's services, one box each, while this one is still the
           person's to apply: the picker carries each one's figures, so it takes
           the read-only table's place. -->
      <section v-if="showPicker" class="policy-preview__band">
        <p class="policy-preview__band-title">{{ $t('adminTitles.services') }}</p>
        <ServicePicker v-model="pickedServices" :items="serviceItems" :disabled="busy"/>
      </section>

      <!-- The services as the carrier lists them. Only the columns some line
           has a value for: a life policy has premiums, a GMM one deductibles. -->
      <section v-else-if="coverages.length" class="policy-preview__band">
        <p class="policy-preview__band-title">{{ $t('adminTitles.services') }}</p>
        <table class="policy-preview__table">
          <tr>
            <th v-for="col in coverageColumns" :key="col.key">{{ col.label }}</th>
          </tr>
          <tr v-for="(c, i) in coverages" :key="i">
            <td v-for="col in coverageColumns" :key="col.key">{{ col.format(c[col.key]) || '—' }}</td>
          </tr>
        </table>
      </section>

      <section v-if="moneyFacts.length" class="policy-preview__band">
        <p class="policy-preview__band-title">{{ $t('carriers.import.preview.Money') }}</p>
        <div class="policy-preview__grid">
          <div v-for="fact in moneyFacts" :key="fact.key" class="policy-preview__fact">
            <span class="caption color--dark-purple">{{ fact.label }}</span>
            <span class="body-text color--black-purple policy-preview__value">
              {{ fact.value || '—' }}
            </span>
          </div>
        </div>
      </section>

      <section v-if="savingsFacts.length" class="policy-preview__band">
        <p class="policy-preview__band-title">
          {{ $t('carriers.import.preview.Savings') }}
          <template v-if="savingsAsOf">· {{ $t('policies.savings.AsOf', { date: savingsAsOf }) }}</template>
        </p>
        <div class="policy-preview__grid">
          <div v-for="fact in savingsFacts" :key="fact.key" class="policy-preview__fact">
            <span class="caption color--dark-purple">{{ fact.label }}</span>
            <span class="body-text color--black-purple policy-preview__value">
              {{ fact.value || '—' }}
            </span>
          </div>
        </div>
      </section>

      <section v-if="beneficiaries.length" class="policy-preview__band">
        <p class="policy-preview__band-title">{{ $t('carriers.import.preview.Beneficiaries') }}</p>
        <table class="policy-preview__table">
          <tr>
            <th>{{ $t('carriers.import.preview.Name') }}</th>
            <th>{{ $t('carriers.import.preview.Relationship') }}</th>
            <th>{{ $t('carriers.import.preview.Share') }}</th>
          </tr>
          <!-- Percentages print exactly as the portal did. Duplicates and totals
               that do not reach 100 are real; the portal returns them. -->
          <tr v-for="(b, i) in beneficiaries" :key="i">
            <td>{{ b.name || '—' }}</td>
            <td>{{ b.relationship || '—' }}</td>
            <td>{{ b.percentage || '—' }}</td>
          </tr>
        </table>
      </section>

      <!-- Who a colectivo covers: each member becomes its own policy under this one
           on apply. Every one of them, however many: a list that stops at fifty and
           says "and 242 more" is no way to check who is on a policy. The band scrolls
           on its own, and a long list gets a filter. A family policy's dependents are
           not saved, so they are not listed. -->
      <section v-if="people.length" class="policy-preview__band">
        <p class="policy-preview__band-title">
          {{ $t('carriers.import.preview.Members', { n: people.length }) }}
        </p>
        <InputSearch
          v-if="people.length > FILTER_FROM"
          class="policy-preview__filter"
          :value="peopleQuery"
          :placeholder="$t('carriers.import.preview.FilterPeople')"
          :opened="true"
          @searchUpdate="peopleQuery = $event"
        />
        <div class="policy-preview__scroll">
          <table class="policy-preview__table">
            <thead>
              <tr>
                <th>{{ $t('carriers.import.preview.Certificate') }}</th>
                <th>{{ $t('carriers.import.preview.Name') }}</th>
                <th>{{ $t('carriers.import.preview.Relationship') }}</th>
                <th>{{ $t('carriers.import.preview.BirthDate') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(m, i) in shownPeople" :key="i">
                <td>{{ m.certificate || '—' }}</td>
                <td>{{ m.name || '—' }}</td>
                <td>{{ m.relationship || '—' }}</td>
                <td>{{ formatCarrierDate(m.birth_date && m.birth_date.iso) || '—' }}</td>
              </tr>
            </tbody>
          </table>
          <p v-if="people.length && !shownPeople.length" class="policy-preview__more">
            {{ $t('carriers.import.preview.NobodyMatches') }}
          </p>
        </div>
      </section>
    </template>

    <p v-else class="policy-preview__empty">{{ $t('carriers.import.preview.Unavailable') }}</p>

    <p v-if="failure" class="policy-preview__failure">{{ failure }}</p>

    <template #footer>
      <p class="policy-preview__note">{{ $t('carriers.import.preview.Note') }}</p>
      <BaseButton type="outline" @click="$emit('close')">{{ $t('buttons.back') }}</BaseButton>
      <!-- Only while the row is still the person's to decide. -->
      <BaseButton
        v-if="canApply && row.reviewStatus === 'PENDING' && incoming"
        :disabled="busy || loading"
        :loading="busy"
        @click="apply"
      >{{ $t('carriers.import.diff.ApplyThisOne') }}</BaseButton>
    </template>
  </BaseSideModal>
</template>

<script>
import BaseSideModal from '@/views/RenovaDashboard/components/ui-kit/BaseSideModal/BaseSideModal';
import BaseButton from '@/views/RenovaDashboard/components/ui-kit/Buttons/BaseButton';
import BasePreloader from '@/views/RenovaDashboard/components/ui-kit/Buttons/BasePreloader';
import InputSearch from '@/views/RenovaDashboard/components/ui-kit/Inputs/InputSearch';
import { UserPermissions } from '@/utils/permissions';
import { apiError } from '@/utils/carrierImport';
import {
  formatCarrierDate, formatCarrierMoney, pickedServiceNames, serviceItems,
} from '@/utils/carrierFormat';
import { missedContributions } from '@/utils/policySavings';
import ServicePicker from './ServicePicker';

/** A list longer than this gets a filter box above it. */
const FILTER_FROM = 50;

/** A coverage line's figure, or nothing: 0 is a value, a blank is not. */
const has = (v) => v !== null && v !== undefined && v !== '';

/**
 * A policy the carrier has and your book does not, as it would be created.
 *
 * Applying from here exists for the one choice the list cannot make: which of
 * the carrier's services this policy should get, ticked one by one. Ticking and
 * applying many at once stays in the list behind this, which never touches
 * services.
 *
 * Everything here comes from the payload the read captured, so it is what the
 * carrier said — not what Renova would infer. Values format through the shared
 * carrier formatters, because a preview that formats money differently from the
 * diff beside it invites a reader to see changes that are not there.
 */
export default {
  name: 'SnapshotPolicyPreviewModal',
  components: {
    BaseSideModal, BaseButton, BasePreloader, InputSearch, ServicePicker,
  },
  mixins: [UserPermissions],
  props: {
    /** The staged row to preview; only its id is needed. */
    row: { type: Object, required: true },
    carrierName: { type: String, default: '' },
  },
  emits: ['close', 'resolved'],
  data() {
    return {
      incoming: null,
      loading: true,
      peopleQuery: '',
      FILTER_FROM,
      /** The carrier's services: {add, update, remove} names and maybe `items`, or null. */
      services: null,
      /** The services ticked to apply. None by default. */
      pickedServices: [],
      busy: false,
      failure: '',
    };
  },
  computed: {
    client() {
      return (this.incoming && this.incoming.client) || {};
    },
    policy() {
      return (this.incoming && this.incoming.policy) || {};
    },
    finance() {
      return (this.incoming && this.incoming.finance) || {};
    },
    canApply() {
      return this.checkUserPermissions([this.PermissionsTypes.POLICIES_UPDATE]);
    },
    /** A colectivo's members. A family policy's dependents are not saved, so not listed. */
    people() {
      return (this.incoming && this.incoming.members) || [];
    },
    coverages() {
      const list = this.incoming && this.incoming.coverages;
      return Array.isArray(list) ? list.filter(Boolean) : [];
    },
    coverageColumns() {
      const money = (v) => (has(v) ? formatCarrierMoney(v) : '');
      return [
        { key: 'description', label: this.$t('policies.caratulaOcr.CoverageName'), format: (v) => (has(v) ? String(v) : '') },
        { key: 'sum_assured', label: this.$t('policies.caratulaOcr.SumInsured'), format: money },
        { key: 'deductible', label: this.$t('policies.caratulaOcr.Deductible'), format: money },
        { key: 'coinsurance_pct', label: this.$t('carriers.import.preview.Coinsurance'), format: (v) => (has(v) ? `${v} %` : '') },
        { key: 'coinsurance_cap', label: this.$t('carriers.import.preview.CoinsuranceCap'), format: money },
        { key: 'premium', label: this.$t('dashboard.Premium'), format: money },
      ].filter((col) => this.coverages.some((c) => has(c[col.key])));
    },
    vehicleFacts() {
      const v = this.policy.vehicle;
      if (!v || typeof v !== 'object') return [];
      return [
        ['description', 'adminTitles.VehicleDescription'],
        ['brand', 'adminTitles.VehicleBrand'],
        ['year', 'adminTitles.VehicleYear'],
        ['plate', 'adminTitles.VehiclePlate'],
        ['vin', 'adminTitles.VehicleVin'],
        ['engine_number', 'adminTitles.VehicleEngineNumber'],
        ['use', 'adminTitles.VehicleUse'],
        ['category', 'adminTitles.VehicleCategory'],
      ]
        .filter(([key]) => has(v[key]) && String(v[key]).trim())
        .map(([key, label]) => ({ key, label: this.$t(label), value: String(v[key]).trim() }));
    },
    serviceItems() {
      return serviceItems(this.services);
    },
    /** Only where a tick can take effect: there are services, and Apply is on offer. */
    showPicker() {
      return this.serviceItems.length > 0 && this.canApply && this.row.reviewStatus === 'PENDING';
    },
    // Everyone, narrowed by the filter box: name, certificate or relationship.
    shownPeople() {
      const q = (this.peopleQuery || '').trim().toLowerCase();
      if (!q) return this.people;
      return this.people.filter((m) => [m.name, m.certificate, m.relationship]
        .some((v) => v && String(v).toLowerCase().includes(q)));
    },
    beneficiaries() {
      return (this.incoming && this.incoming.beneficiaries) || [];
    },
    holderFacts() {
      // A colectivo is held by its company: there is no person, and none of a
      // person's identifiers (RFC, e-mail, phone) apply, so only the company shows.
      const company = this.client.company && this.client.company.name;
      if (company) {
        return [
          { key: 'company', label: this.$t('carriers.import.preview.Company'), value: company },
        ];
      }
      return [
        { key: 'name', label: this.$t('carriers.import.preview.Name'), value: this.client.name },
        { key: 'rfc', label: this.$t('carriers.import.preview.Rfc'), value: this.client.rfc },
        { key: 'email', label: this.$t('carriers.import.preview.Email'), value: this.client.email },
        { key: 'phone', label: this.$t('carriers.import.preview.Phone'), value: this.client.phone },
        { key: 'address', label: this.$t('carriers.import.preview.Address'), value: this.client.address },
      ];
    },
    policyFacts() {
      return [
        {
          key: 'number',
          label: this.$t('carriers.import.preview.PolicyNumber'),
          value: this.policy.number,
        },
        {
          key: 'product',
          label: this.$t('adminTitles.program'),
          value: this.policy.product,
        },
        {
          // The carrier's line of business (SALUD, VIDA, …) — what the policy is filed as.
          key: 'type',
          label: this.$t('carriers.import.preview.Type'),
          value: this.incoming && this.incoming.type,
        },
        {
          key: 'start',
          label: this.$t('carriers.import.preview.StartDate'),
          value: formatCarrierDate(this.policy.start_date && this.policy.start_date.iso),
        },
        {
          key: 'end',
          label: this.$t('carriers.import.preview.EndDate'),
          value: formatCarrierDate(this.policy.end_date && this.policy.end_date.iso),
        },
        {
          key: 'format',
          label: this.$t('carriers.import.preview.PaymentFormat'),
          value: this.policy.payment_format,
        },
        {
          // What the client pays a year, off the carrier's document (AXA carátula).
          key: 'premium',
          label: this.$t('leads.form.dealPremium'),
          value: formatCarrierMoney(this.policy.annual_premium && this.policy.annual_premium.value),
        },
        {
          key: 'netPremium',
          label: this.$t('leads.form.netPremium'),
          value: formatCarrierMoney(this.policy.net_premium && this.policy.net_premium.value),
        },
      ];
    },
    moneyFacts() {
      const f = this.finance;
      if (!f || !Object.keys(f).length) return [];
      return [
        {
          key: 'balance',
          label: this.$t('carriers.import.preview.Balance'),
          value: formatCarrierMoney(f.total_balance && f.total_balance.value),
        },
        {
          key: 'contributions',
          label: this.$t('carriers.import.preview.Contributions'),
          value: formatCarrierMoney(
            f.accumulated_contributions && f.accumulated_contributions.value,
          ),
        },
        {
          key: 'monthly',
          label: this.$t('carriers.import.preview.MonthlyDeposit'),
          value: formatCarrierMoney(
            (f.initial_monthly_deposit && f.initial_monthly_deposit.value)
              || (f.monthly_deposit && f.monthly_deposit.value),
          ),
        },
        {
          key: 'currentMonthly',
          label: this.$t('carriers.import.preview.CurrentMonthlyDeposit'),
          value: formatCarrierMoney(
            f.current_monthly_deposit && f.current_monthly_deposit.value,
          ),
        },
      ];
    },
    savings() {
      return (this.incoming && this.incoming.savings) || null;
    },
    savingsAsOf() {
      const s = this.savings;
      return s && s.as_of && s.as_of.iso ? formatCarrierDate(s.as_of.iso) : '';
    },
    // A savings plan's figures as the carrier stated them (Skandia), and how
    // many receipts the read captured with it. Empty for any other policy.
    savingsFacts() {
      const s = this.savings;
      const receipts = this.incoming && Array.isArray(this.incoming.payments)
        ? this.incoming.payments.length
        : null;
      const facts = [];
      if (s) {
        const missed = missedContributions(s.pending_contributions);
        let missedText = '';
        if (missed !== null) {
          missedText = missed > 0 ? String(missed) : this.$t('policies.savings.UpToDate');
        }
        facts.push(
          {
            key: 'balance',
            label: this.$t('policies.savings.Balance'),
            value: formatCarrierMoney(s.balance && s.balance.value),
          },
          {
            key: 'contributed',
            label: this.$t('policies.savings.Contributed'),
            value: formatCarrierMoney(s.contributed && s.contributed.value),
          },
          {
            key: 'missed',
            label: this.$t('policies.savings.MissedContributions'),
            value: missedText,
          },
        );
      }
      if (receipts !== null) {
        facts.push({
          key: 'receipts',
          label: this.$t('carriers.import.preview.Receipts'),
          value: String(receipts),
        });
      }
      return facts;
    },
  },
  methods: {
    formatCarrierDate,
    /**
     * Add this one policy, with the services ticked here: they ride only on
     * this one-row apply.
     * A row that could not be applied stays open with its reason.
     */
    apply() {
      const { id } = this.row;
      if (!id || this.busy) return;
      this.busy = true;
      this.failure = '';
      const names = this.showPicker ? pickedServiceNames(this.serviceItems, this.pickedServices) : [];
      const payload = names.length ? { ids: [id], services: [{ id, names }] } : [id];
      this.$store.dispatch('carrierImportApplySnapshot', payload)
        .then((res) => {
          if (!(res && res.appliedCount)) {
            const failure = ((res && res.failures) || [])[0];
            this.failure = (failure && failure.message) || this.$t('carriers.import.diff.CouldNotApply');
            return;
          }
          // A read nobody pressed Read for: the accept queued it so the policy just
          // added gets its receipts. Said, so it is no surprise under All updates.
          if (res.paymentsReads && res.paymentsReads.length) {
            this.$notify.success({
              message: this.$t('carriers.import.snapshot.PaymentsReadQueued', {
                carrier: this.carrierName || this.$t('carriers.import.TheCarrier'),
              }),
            });
          }
          this.$emit('resolved');
        })
        .catch((err) => this.$notify.error({
          message: apiError(err, this.$t('adminTitles.someError')),
        }))
        .finally(() => { this.busy = false; });
    },
  },
  created() {
    // The list omits the payload deliberately -- 200 of them is megabytes -- so
    // a preview fetches the one row it is about.
    this.$store.dispatch('carrierImportFetchSnapshotDetail', this.row.id)
      .then((data) => {
        this.incoming = (data && data.incoming) || null;
        this.services = (data && data.services) || null;
      })
      .catch((err) => this.$notify.error({
        message: apiError(err, this.$t('adminTitles.someError')),
      }))
      .finally(() => { this.loading = false; });
  },
};
</script>

<style lang="scss" scoped>
.policy-preview {
  &__subtitle {
    font-size: 13px;
    font-weight: 400;
    color: var(--solid-dark-purple);
    margin-bottom: 12px;
  }

  &__doc {
    color: var(--solid-purple);
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  &__empty {
    padding: 24px 0;
    font-size: 14px;
    color: var(--solid-dark-purple);
  }

  &__band {
    padding-top: 16px;
    margin-bottom: 16px;
    border-top: 1px solid var(--solid-lavender-grey);

    &:first-of-type { border-top: 0; padding-top: 0; }
  }

  &__band-title {
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: var(--solid-dark-purple);
    margin-bottom: 12px;
  }

  // Label above value, not beside it. The columns-of-240px layout the policy
  // page uses elsewhere cannot hold a long product name without the grid
  // coming apart.
  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 12px 16px;
  }

  &__fact {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  &__value {
    overflow-wrap: anywhere;
    font-variant-numeric: tabular-nums;
  }

  &__table {
    width: 100%;
    font-size: 13px;

    th {
      text-align: left;
      font-weight: 500;
      color: var(--solid-dark-purple);
      padding: 0 12px 6px 0;
    }

    td {
      padding: 4px 12px 4px 0;
      vertical-align: top;
      color: var(--solid-black-purple);
      overflow-wrap: anywhere;
    }
  }

  &__more {
    margin-top: 8px;
    font-size: 12.5px;
    color: var(--solid-dark-purple);
  }

  &__filter {
    width: 280px !important;
    margin-bottom: 8px;
  }

  // The list scrolls inside its band, with the header row kept in view, so a
  // colectivo of three hundred does not push the policy's other facts off screen.
  &__scroll {
    max-height: 60vh;
    overflow-y: auto;

    thead th {
      position: sticky;
      top: 0;
      background: var(--solid-white, #fff);
    }
  }

  &__failure {
    font-size: 12.5px;
    color: var(--solid-red);
    margin-top: 12px;
  }

  &__note {
    margin-right: auto;
    font-size: 12.5px;
    line-height: 1.4;
    color: var(--solid-dark-purple);
  }

  // The cover description is several lines (plan, sum insured, who is insured).
  &__value {
    white-space: pre-line;
  }
}
</style>
